import type { QueryClient } from '@tanstack/react-query';
import { helpKeys } from '@/features/help-exchange/queries';
import { homeKeys } from '@/features/home/queries';
import { broadcastKeys } from '@/features/messaging/broadcastQueries';
import { messagingKeys } from '@/features/messaging/queries';
import { useBannerStore } from '@/state/bannerStore';
import { spotlightKeys } from '@/features/profile/spotlightKeys';
import { registerBinding } from './bindings';

import { tNow } from '@/i18n/languageStore';

/**
 * Live updates (backend RealtimeEvents): the server tells an open app that something changed, so it shows the change
 * without a refresh. The same events reach the phone app (mobile/src/state/messagingStore.ts).
 */
const isString = (value: unknown): value is string => typeof value === 'string';
const postIdOf = (payload: unknown): string | null => {
  const id = (payload as Record<string, unknown> | null)?.postId;
  return isString(id) ? id : null;
};

/** The offers on a post, what the post says about them, and the badge that counts them. */
function reloadOffers(queryClient: QueryClient, postId: string) {
  void queryClient.invalidateQueries({ queryKey: helpKeys.offersWaiting });
  void queryClient.invalidateQueries({ queryKey: helpKeys.offers(postId) });
  void queryClient.invalidateQueries({ queryKey: helpKeys.post(postId) });
}

// A post was made, edited, taken, finished or removed, on any device: Home, Browse and My posts reload.
registerBinding('messaging', 'feed:changed', (_payload, { queryClient }) => {
  void queryClient.invalidateQueries({ queryKey: homeKeys.feed('all').slice(0, 2) });
  void queryClient.invalidateQueries({ queryKey: helpKeys.allPosts });
});

// The member was chosen for the Neighbor spotlight: say so, and how to say no. The profile shows it straight away.
registerBinding('messaging', 'spotlight:featured', (_payload, { queryClient }) => {
  void queryClient.invalidateQueries({ queryKey: spotlightKeys.mine });
  useBannerStore.getState().showCommunity({
    title: tNow('You’re in the Neighbor spotlight'),
    text: tNow('Your neighbors can see you on Home. You can opt out in your profile.'),
  });
});

// An offer arrived, was answered or withdrawn.
registerBinding('messaging', 'offers:changed', (payload, { queryClient }) => {
  const postId = postIdOf(payload);
  if (postId) reloadOffers(queryClient, postId);
});

// A neighbor answered Yes or No to a broadcast I sent: the counts and the lists of who answered reload.
registerBinding('messaging', 'broadcast:answered', (_payload, { queryClient }) => {
  void queryClient.invalidateQueries({ queryKey: broadcastKeys.all });
});

// The poster accepted this member's offer: say so wherever they are, and open the post on a click.
registerBinding('messaging', 'offer:accepted', (payload, { queryClient }) => {
  const p = payload as Record<string, unknown> | null;
  const postId = postIdOf(payload);
  if (!postId || !p || !isString(p.title) || !isString(p.posterName)) return;
  reloadOffers(queryClient, postId);
  // The accepted offer opened a chat.
  void queryClient.invalidateQueries({ queryKey: messagingKeys.allConversations });
  useBannerStore.getState().showCommunity({
    title: tNow('Offer accepted'),
    text: tNow('{posterName} accepted your offer on “{title}”', {
      posterName: p.posterName,
      title: p.title,
    }),
    targetType: 'post',
    targetId: postId,
  });
});
